<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[105] flex items-center justify-center bg-ink/50 px-4 py-6">
      <div ref="dialogPanel" role="dialog" aria-modal="true" :aria-labelledby="dialogTitleId" tabindex="-1" @keydown="onDialogKeydown" class="w-full max-w-md flex flex-col paper-card rounded-md overflow-hidden max-h-[80vh]">
        <div class="flex items-center gap-2 p-3.5 border-b border-[hsl(var(--border)/0.6)] shrink-0">
          <p :id="dialogTitleId" class="flex-1 text-[15px] font-semibold truncate">{{ t('map.routeEditor.importDialog.title') }}</p>
          <button
            type="button"
            :aria-label="t('map.routeEditor.importDialog.close')"
            class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors"
            @click="$emit('cancel')"
          ><X class="w-4 h-4" /></button>
        </div>

        <div role="tablist" class="flex gap-1 px-3 pt-2.5 shrink-0 overflow-x-auto">
          <button
            v-for="tabItem in tabs"
            :key="tabItem.id"
            type="button"
            role="tab"
            :data-import-tab="tabItem.id"
            :aria-selected="tab === tabItem.id"
            class="shrink-0 h-7 px-2.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
            :class="tab === tabItem.id ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--muted)/0.5)] text-ink2'"
            @click="tab = tabItem.id"
          >{{ tabItem.label }}</button>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto px-3 py-3">
          <template v-if="tab !== 'gpx'">
            <div v-if="!listForTab.length" class="py-8 text-center text-[12px] text-ink2">
              {{ emptyLabel }}
            </div>
            <button
              v-for="route in listForTab"
              :key="route.id"
              type="button"
              class="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-md hover:bg-[hsl(var(--muted)/0.5)] text-left"
              @click="selectRoute(route)"
            >
              <span class="min-w-0 flex-1">
                <span class="block text-[12.5px] font-semibold truncate">{{ route.name }}</span>
                <span class="block text-[10.5px] text-ink2">{{ route.distance_km?.toFixed(1) }} {{ t('units.km') }}</span>
              </span>
              <ChevronRight class="w-4 h-4 text-ink2 shrink-0" />
            </button>
          </template>

          <template v-else>
            <label class="flex flex-col items-center justify-center gap-2 py-8 rounded-md border-2 border-dashed border-[hsl(var(--border))] cursor-pointer hover:bg-[hsl(var(--muted)/0.3)]">
              <Upload class="w-6 h-6 text-ink2" />
              <span class="text-[12.5px] font-semibold">{{ t('map.routeEditor.importDialog.uploadButton') }}</span>
              <span class="text-[10.5px] text-ink2">{{ t('map.routeEditor.importDialog.uploadHint') }}</span>
              <input type="file" accept=".gpx" class="hidden" @change="onFileChange" />
            </label>
            <p v-if="gpxError" class="mt-2 text-[11px] text-[hsl(var(--danger))]">{{ gpxError }}</p>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useDialogA11y } from "../composables/useDialogA11y";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { X, ChevronRight, Upload } from "lucide-vue-next";
import { useMyRoutesStore } from "../store/myRoutes";
import { useSavedRoutesStore } from "../store/savedRoutes";
import { useRoutesStore } from "../store/routes";
import { parseGpxText, readFileAsText } from "../utils/gpxImport";
import type { RouteMapItem } from "../types/routes";

export type ImportDialogTab = "mine" | "favorites" | "public" | "gpx";

// initialTab - вкладка, выбранная в меню «Импорт» редактора.
const props = withDefaults(defineProps<{ initialTab?: ImportDialogTab }>(), { initialTab: "mine" });

const emit = defineEmits<{
  (e: "cancel"): void;
  (e: "select-route", route: RouteMapItem, asNewCopy: boolean): void;
  (e: "select-gpx", name: string, points: [number, number][], waypoints: ReturnType<typeof parseGpxText>["waypoints"]): void;
}>();

const { t } = useI18n();
const myRoutes = useMyRoutesStore();
const savedRoutes = useSavedRoutesStore();
const routes = useRoutesStore();

const tab = ref<ImportDialogTab>(props.initialTab);
const tabs = computed(() => [
  { id: "mine" as const, label: t("map.routeEditor.importDialog.tabMine") },
  { id: "favorites" as const, label: t("map.routeEditor.importDialog.tabFavorites") },
  { id: "public" as const, label: t("map.routeEditor.importDialog.tabPublic") },
  { id: "gpx" as const, label: t("map.routeEditor.importDialog.tabGpx") },
]);

onMounted(() => {
  myRoutes.fetch();
  savedRoutes.fetchIds();
  routes.fetch();
});

const listForTab = computed<RouteMapItem[]>(() => {
  if (tab.value === "mine") return myRoutes.items;
  if (tab.value === "favorites") return routes.items.filter((r) => savedRoutes.ids.includes(r.id));
  if (tab.value === "public") return routes.items;
  return [];
});

const emptyLabel = computed(() => {
  if (tab.value === "mine") return t("map.routeEditor.importDialog.emptyMine");
  if (tab.value === "favorites") return t("map.routeEditor.importDialog.emptyFavorites");
  return t("map.routeEditor.importDialog.emptyPublic");
});

const selectRoute = (route: RouteMapItem) => {
  emit("select-route", route, tab.value !== "mine");
};

const gpxError = ref("");

const onFileChange = async (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  gpxError.value = "";
  try {
    const text = await readFileAsText(file);
    const parsed = parseGpxText(text);
    const name = file.name.replace(/\.gpx$/i, "");
    emit("select-gpx", name, parsed.points, parsed.waypoints);
  } catch {
    gpxError.value = t("map.routeEditor.importDialog.importError");
  }
};

// Доступность окна: ловушка фокуса, Esc, возврат фокуса (useDialogA11y).
const dialogPanel = ref<HTMLElement | null>(null);
const { titleId: dialogTitleId, onKeydown: onDialogKeydown } = useDialogA11y(dialogPanel, { onClose: () => emit('cancel') });
</script>
